import React, { useMemo } from 'react';
import stopIcon from '../../img/_Stop.png';
import yieldIcon from '../../img/_Yield.png';
import goIcon from '../../img/_Go.png';
import closeIcon from '../../img/pretty-close-2.png';
import styles from './ExpandedMatchBlockStylespdf';

// ---------- helpers ----------
const EM_DASH = '—';

const parseNumberWithUnit = (val) => {
  if (val === 0) return { num: 0, unit: '' };
  if (!val || typeof val !== 'string') return { num: NaN, unit: '' };
  const cleaned = val.replace(/[()]/g, '').replace(/,/g, '').trim();
  const m = cleaned.match(/^(-?[\d.]+)\s*([a-zA-Z%°\/]*)$/);
  if (!m) return { num: NaN, unit: '' };
  let num = parseFloat(m[1]);
  if (val.includes('(') && val.includes(')')) num *= -1; // treat (x) as negative
  return { num, unit: m[2] || '' };
};

const HEADER_SYNONYMS = {
  qty: 'QTY', quantity: 'QTY', quant: 'QTY',
  total: 'TOTAL', acv: 'ACV', rcv: 'RCV',
  desc: 'Description', description: 'Description',
};

const normalizeHeader = (h) => {
  if (!h) return '';
  const key = h.trim().toLowerCase();
  return HEADER_SYNONYMS[key] || h.charAt(0).toUpperCase() + h.slice(1);
};

const unifyHeaders = (h1, h2) => {
  const a = h1.slice(1).map(normalizeHeader);   // drop any index column
  const b = h2.slice(1).map(normalizeHeader);
  const unique = Array.from(new Set([...a, ...b]));
  const lastCandidates = ['TOTAL', 'ACV', 'RCV'];
  const last = lastCandidates.find((c) => unique.includes(c));
  return last ? [...unique.filter((x) => x !== last), last] : unique;
};

const getValueByHeader = (values, header) => {
  const canonical = HEADER_SYNONYMS[header.toLowerCase()] || header;
  const keys = Object.keys(values || {});
  const k = keys.find(
    (x) =>
      HEADER_SYNONYMS[x.toLowerCase()] === canonical ||
      x.toLowerCase() === canonical.toLowerCase()
  );
  const v = k ? values[k] : '';
  return v === '' || v === undefined || v === null ? EM_DASH : v;
};

const moneyHeaders = ['TOTAL', 'ACV', 'RCV'];

// signed, unit-aware (used in the ∆ row)
const formatSignedCell = (raw, header) => {
  const { num, unit } = parseNumberWithUnit(String(raw));
  if (isNaN(num)) return EM_DASH;
  if (moneyHeaders.includes(header.toUpperCase())) {
    return Math.abs(num).toLocaleString('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
    });
  }
  const fixed = num.toFixed(2);
  const tightUnits = new Set(['hr', 'ft', 'in']);
  const sep = unit && !tightUnits.has(unit) ? ' ' : '';
  return `${fixed}${unit ? sep + unit : ''}`;
};

// absolute money (for the centered trio)
const formatMoneyAbs = (raw) => {
  const { num } = parseNumberWithUnit(String(raw));
  if (isNaN(num)) return EM_DASH;
  return Math.abs(num).toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
  });
};

const colorForDelta = (raw) => {
  const { num } = parseNumberWithUnit(String(raw));
  if (isNaN(num)) return styles.neutral;
  return num > 0 ? styles.negative : styles.positive;
};

// ---------- page-top components (render ONCE per document) ----------
export const ReportDocs = ({ docname1, priceList1, docname2, priceList2 }) => (
  <div style={styles.docsTopLeft}>
    <div>
      <span style={styles.docLabel}>DOC 1:&nbsp;</span>
      <span style={styles.docValue}>{docname1 || EM_DASH}</span>
      {priceList1 ? <span style={styles.priceList}>&nbsp;|&nbsp;Price List: {priceList1}</span> : null}
    </div>
    <div>
      <span style={styles.docLabel}>DOC 2:&nbsp;</span>
      <span style={styles.docValue}>{docname2 || EM_DASH}</span>
      {priceList2 ? <span style={styles.priceList}>&nbsp;|&nbsp;Price List: {priceList2}</span> : null}
    </div>
  </div>
);

export const ReportKey = () => (
  <div style={styles.keyTop}>
    <span style={styles.keyTitle}>KEY:&nbsp;</span>
    <span style={styles.keyItem}><img src={goIcon} alt="Match" style={styles.keyIcon}/> Match Found - Math is zero</span>
    <span style={styles.keyItem}><img src={closeIcon} alt="Close" style={styles.keyIcon}/> Match Found - Math is nonzero</span>
    <span style={styles.keyItem}><img src={yieldIcon} alt="Similar" style={styles.keyIcon}/> Similar Item Found</span>
    <span style={styles.keyItem}><img src={stopIcon} alt="No Match" style={styles.keyIcon}/> No Match Found</span>
  </div>
);

// ---------- per-match block ----------
const ExpandedMatchBlock = ({
  match,
  cleanParsedEstData,
  cleanParsedInsData,
  headersOne,
  headersTwo,
}) => {
  const system = match['User Modified'];
  const doc1Ids = system?.doc1?.ids || [];
  const doc2Ids = system?.doc2?.ids || [];
  const designation = system?.Designation;

  const unifiedHeaders = useMemo(
    () => unifyHeaders(headersOne, headersTwo),
    [headersOne, headersTwo]
  );

  const getCrossColumnFinancialDeltas = () => {
    const headers = ['ACV', 'RCV', 'Total'];
    const results = [];
    for (let h1 of headers) {
      let sum1 = 0, unit1Set = new Set(), valid1 = false;
      doc1Ids.forEach(id => {
        const item = cleanParsedEstData.lineItems.find(obj => obj[id]);
        if (item) {
          const val = getValueByHeader(item[id].values || {}, h1);
          const { num, unit } = parseNumberWithUnit(val);
          if (!isNaN(num)) {
            sum1 += num;
            unit1Set.add(unit);
            valid1 = true;
          }
        }
      });
      for (let h2 of headers) {
        let sum2 = 0, unit2Set = new Set(), valid2 = false;
        doc2Ids.forEach(id => {
          const item = cleanParsedInsData.lineItems.find(obj => obj[id]);
          if (item) {
            const val = getValueByHeader(item[id].values || {}, h2);
            const { num, unit } = parseNumberWithUnit(val);
            if (!isNaN(num)) {
              sum2 += num;
              unit2Set.add(unit);
              valid2 = true;
            }
          }
        });
        const unitsMatch = unit1Set.size <= 1 && unit2Set.size <= 1 &&
          [...unit1Set][0] === [...unit2Set][0];
        const unit = unitsMatch ? [...unit1Set][0] || '' : '';
        if (valid1 && valid2 && unitsMatch) {
          const diff = (sum1 - sum2).toFixed(2);
          results.push(`${h1} - ${h2}: ${diff} ${unit}`.trim());
        }
      }
    }
    return results;
  };  

  const lastIdx = unifiedHeaders.length - 1; // <-- TOTAL column index

  // per-column totals ∆ (doc1 - doc2)
  const columnDeltas = useMemo(() => {
    return unifiedHeaders.map((header) => {
      let sum1 = 0, sum2 = 0;
      let unit1 = new Set(), unit2 = new Set();
      let v1 = false, v2 = false;

      doc1Ids.forEach((id) => {
        const obj = cleanParsedEstData.lineItems.find((o) => o[id]);
        if (obj) {
          const { num, unit } = parseNumberWithUnit(getValueByHeader(obj[id].values || {}, header));
          if (!isNaN(num)) { sum1 += num; unit1.add(unit); v1 = true; }
        }
      });

      doc2Ids.forEach((id) => {
        const obj = cleanParsedInsData.lineItems.find((o) => o[id]);
        if (obj) {
          const { num, unit } = parseNumberWithUnit(getValueByHeader(obj[id].values || {}, header));
          if (!isNaN(num)) { sum2 += num; unit2.add(unit); v2 = true; }
        }
      });

      const unitsMatch = unit1.size <= 1 && unit2.size <= 1 && [...unit1][0] === [...unit2][0];
      const unit = unitsMatch ? ([...unit1][0] || '') : '';
      if (!v1 && !v2) return EM_DASH;
      if (!unitsMatch)   return EM_DASH;
      return `${(sum1 - sum2).toFixed(2)} ${unit}`.trim();
    });
  }, [cleanParsedEstData, cleanParsedInsData, doc1Ids, doc2Ids, unifiedHeaders]);

  const collectDescriptions = (ids, data) =>
    ids
      .map((id) => {
        const obj = data.lineItems.find((o) => o[id]);
        return obj ? getValueByHeader(obj[id].values || {}, 'Description') : '';
      })
      .filter(Boolean)
      .join('; ');

  const firstZoneFrom = (ids, data) => {
    const first = data.lineItems.find((o) => ids.includes(Object.keys(o)[0]));
    const key = first ? Object.keys(first)[0] : null;
    const item = key ? first[key] : null;
    return item?.userModifiedZone || item?.detectedZone || EM_DASH;
  };

  const statusIcon =
    designation === 'Match'
      ? goIcon
      : designation === 'Close'
      ? closeIcon
      : designation === 'Similar'
      ? yieldIcon
      : stopIcon;

  // one grid section
  const renderGrid = (ids, data, rowNo, showHeader = true) => {
    const safeIds = ids.length ? ids : ['__placeholder__'];

    return (
      <div style={styles.gridBlock}>
        {showHeader && (
          <div style={styles.gridHeadRow}>
            <div style={styles.rowNoHeadSpacer} />
            {unifiedHeaders.map((h, i) => (
              <div
                key={`h-${i}`}
                style={{
                  ...styles.gridHeadCell,
                  ...(i % 2 ? styles.oddCell : styles.evenCell),
                  fontWeight: i === lastIdx ? 400 : 700, // TOTAL header not bold
                }}
              >
                {h}
              </div>
            ))}
          </div>
        )}

        {safeIds.map((id) => {
          const obj = data.lineItems.find((o) => o[id]);
          const vals = obj?.[id]?.values || {};
          const isPlaceholder = id === '__placeholder__';
          return (
            <div key={`r-${id}`} style={styles.gridValRow}>
              <div style={styles.rowNoCell}>{rowNo}.</div>
              {unifiedHeaders.map((h, i) => (
                <div
                  key={`v-${id}-${i}`}
                  style={{
                    ...styles.gridValCell,
                    ...(i % 2 ? styles.oddCell : styles.evenCell),
                    fontWeight: i === lastIdx ? 700 : 400, // TOTAL value bold
                  }}
                >
                  {isPlaceholder ? EM_DASH : getValueByHeader(vals, h)}
                </div>
              ))}
            </div>
          );
        })}
      </div>
    );
  };

  // footer inputs
  const desc1 = collectDescriptions(doc1Ids, cleanParsedEstData) || EM_DASH;
  const desc2 = collectDescriptions(doc2Ids, cleanParsedInsData) || EM_DASH;
  const zone1 = firstZoneFrom(doc1Ids, cleanParsedEstData);
  const zone2 = firstZoneFrom(doc2Ids, cleanParsedInsData);

  const idxRCV = unifiedHeaders.findIndex((h) => h.toUpperCase() === 'RCV');
  const idxACV = unifiedHeaders.findIndex((h) => h.toUpperCase() === 'ACV');
  const idxTOT = unifiedHeaders.findIndex((h) => h.toUpperCase() === 'TOTAL');
  const valRCV = idxRCV > -1 ? columnDeltas[idxRCV] : EM_DASH;
  const valACV = idxACV > -1 ? columnDeltas[idxACV] : EM_DASH;
  const valTOT = idxTOT > -1 ? columnDeltas[idxTOT] : EM_DASH;

  return (
    <section style={styles.section}>
      {/* right-side status icon */}
      <img src={statusIcon} alt="status" style={styles.statusBubble} />

      {/* DOC 1 with headers */}
      {renderGrid(doc1Ids, cleanParsedEstData, 1, true)}

      {/* DOC 2 without headers (aligned) */}
      {renderGrid(doc2Ids, cleanParsedInsData, 2, false)}

      {/* per-column ∆ row (aligned like a third row, dotted top border) */}
      <div style={styles.deltaRow}>
        <div style={styles.rowNoCell} />
        {unifiedHeaders.map((h, i) => (
          <div
            key={`d-${i}`}
            style={{
              ...styles.gridValCell,
              ...(i % 2 ? styles.oddCell : styles.evenCell),
              fontWeight: i === lastIdx ? 700 : 400, // TOTAL ∆ bold
            }}
          >
            {formatSignedCell(columnDeltas[i], h)}
          </div>
        ))}
      </div>

      {/* footer meta in one line: notes | descriptions | zones */}
      <div style={styles.footerMetaRow}>
        <div style={styles.metaNotes}>
          <span>NOTES:</span>&nbsp;
          <span>{match?.Notes || ''}</span>
        </div>

        <div style={styles.metaDescs}>
          <div><span style={styles.metaStrong}>DOC 1 DESC:</span>&nbsp;{desc1}</div>
          <div><span style={styles.metaStrong}>DOC 2 DESC:</span>&nbsp;{desc2}</div>
        </div>

        <div style={styles.metaZones}>
          <div><span style={styles.metaStrong}>DOC 1 ZONE:</span>&nbsp;{zone1}</div>
          <div><span style={styles.metaStrong}>DOC 2 ZONE:</span>&nbsp;{zone2}</div>
        </div>
      </div>

      <div style={styles.footerCenteredTrio}>

        {getCrossColumnFinancialDeltas().map((line, i) => {
          const [label, value] = line.split(/:(.+)/); // split on first colon only
          const { num: numericValue } = parseNumberWithUnit(String(value || ''));

          // Transform "RCV - Total" → "∆(RCV, Total)"
          const transformedLabel = label.replace(/(.+?) - (.+)/, '∆($1, $2)');

          // Always display the absolute value as a dollar amount
          const displayValue = Number.isFinite(numericValue)
            ? Math.abs(numericValue).toLocaleString('en-US', {
                style: 'currency',
                currency: 'USD',
                minimumFractionDigits: 2,
              })
            : EM_DASH;

          return (
            <span key={i} style={{ fontSize: 17 }}>
              <span
                style={{
                  color: '#425158',
                  padding: '0px 2px',
                  fontWeight: 'bold',
                }}
              >
                {transformedLabel}:
              </span>{' '}
              <span
                style={{
                  color: Number.isFinite(numericValue)
                    ? (numericValue > 0 ? 'red' : 'green')
                    : '#839299',
                  padding: '0px 2px',
                  fontWeight: '900',
                }}
              >
                {displayValue}
              </span>
            </span>
          );
        })}
      </div>
    </section>
  );
};

export default ExpandedMatchBlock;
